import { Button, Flex, Text, Grid, Em, Link } from "@radix-ui/themes";
import { FieldGroup } from "../parts/FieldGroup";
import { useCopilotConfiguration } from "@/api/quackchat/configuration/useCopilotConfiguration";
import { Spinner } from "@radix-ui/themes";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import Switch from "@/components/shared/inputs/switch";
import { SortableItem } from "@/v2/components/sortable";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { Divider } from "@geist-ui/core";
import { Copilot } from "@/v2/components/copilot";
import { useGetAllConfigurationBlocks } from "@/api/copilot";
import { useAuth0 } from "@auth0/auth0-react";
import { Dialog } from "@/components/shared/dialog";
import { useIntegrations } from "@/api/integration/Integration.api";
import { integrationNameToGalleryItem } from "@/api/integration/integrationNameToGalleryItem";
import { MultiSelect, SelectOption } from "../parts/MultiSelect";
import { useNavigate } from "react-router-dom";

enum BlockType {
  USER_SENTIMENT = "userSentiment",
  ORGANIZATION_ANALYSIS = "organizationAnlaysis",
  TICKET_SUMMARY = "ticketSummary",
  PREVIOUS_TICKET_RESOLUTION = "previousTicketResolution",
  SUGGESTED_SCENARIOS = "suggestedScenarios",
  TRANSLATION = "translation",
  ASSOCIATED_JIRA_TICKETS = "associatedJiraTickets",
  ASK_ANYTHING = "askAnything",
}

const BlocksMapper = {
  userSentiment: {
    name: "User Sentiment",
    description: "Analyze user sentiment",
    view: "main",
  },
  organizationAnlaysis: {
    name: "Organization Analysis",
    description: "Analyze organization",
    view: "main",
  },
  ticketSummary: {
    name: "Ticket Summary",
    description: "Analyze ticket summary",
    view: "main",
  },
  previousTicketResolution: {
    name: "Previous Ticket Resolution",
    description: "Analyze previous ticket resolution",
    view: "main",
  },
  suggestedScenarios: {
    name: "Suggested Scenarios",
    description: "Analyze suggested scenarios",
    view: "main",
  },
  translation: {
    name: "Translation",
    description: "Translate text",
    view: "main",
  },
  associatedJiraTickets: {
    name: "Associated Jira Tickets",
    description: "Analyze associated Jira tickets",
    view: "main",
  },
  askAnything: {
    name: "Ask Anything",
    description: "Ask anything",
    view: "main",
  },
};

const Blocks = [
  {
    id: 1,
    type: "userSentiment",
    name: "User Sentiment",
    description: "Analyze user sentiment",
    view: "main",
  },
  // {
  //   id: 2,
  //   type: "organizationAnlaysis",
  //   name: "Organization Analysis",
  //   description: "Analyze organization",
  //   view: "main",
  // },
  {
    id: 4,
    type: "ticketSummary",
    name: "Ticket Summary",
    description: "Analyze ticket summary",
    view: "main",
  },
  {
    id: 5,
    type: "previousTicketResolution",
    name: "Previous Ticket Resolution",
    description: "Analyze previous ticket resolution",
    view: "main",
  },
  {
    id: 6,
    type: "suggestedScenarios",
    name: "Suggested Scenarios",
    description: "Analyze suggested scenarios",
    view: "main",
  },
  {
    id: 7,
    type: "translation",
    name: "Translation",
    description: "Translate text",
    view: "main",
  },
  // {
  //   id: 8,
  //   type: "associatedJiraTickets",
  //   name: "Associated Jira Tickets",
  //   description: "Analyze associated Jira tickets",
  //   view: "main",
  // },
  {
    id: 10,
    type: "askAnything",
    name: "Ask Anything",
    description: "Ask anything",
    view: "main",
    defaultLocation: true,
  },
];

function CopilotBlockDialog({
  items,
  setItems,
  selectedView,
  setDialogOpen,
  dialogOpen,
}: {
  items: any[];
  setItems: Dispatch<
    SetStateAction<
      {
        id: string;
        type: string;
        defaultLocation?: boolean;
        view: string;
      }[]
    >
  >;
  selectedView: string;
  setDialogOpen: (open: boolean) => void;
  dialogOpen: boolean;
}) {
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);

  function handleAddBlocks() {
    if (selectedBlocks.length > 0) {
      setItems((prev) => [
        ...prev,
        ...selectedBlocks.map((blockType) => ({
          id: `${Date.now() + Math.random()}`,
          type: blockType,
          view: selectedView,
        })),
      ]);
      setDialogOpen(false);
      setSelectedBlocks([]);
    }
  }

  function toggleBlockSelection(blockType: string) {
    setSelectedBlocks((prev) =>
      prev.includes(blockType)
        ? prev.filter((type) => type !== blockType)
        : [...prev, blockType]
    );
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <Dialog.Content size="large">
        <Dialog.Close className=" absolute top-4 right-4">
          <Button variant="soft" color="gray">
            Cancel
          </Button>
        </Dialog.Close>
        <Flex direction={"column"} gap={"2"} className=" p-4">
          <Dialog.Title>Add Copilot Blocks</Dialog.Title>
          <Text size="2" mb="4">
            Select one or more block types to add to your copilot view.
          </Text>
          <Grid columns={"2"} gap={"3"} className="mb-4">
            {Blocks.filter((x) => {
              if (items.some((i) => i.type === x.type)) {
                return false;
              }
              return true;
            })?.map((block: any) => (
              <Flex
                key={block.type}
                className={`p-3 border rounded-md cursor-pointer ${
                  selectedBlocks.includes(block.type)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200"
                }`}
                onClick={() => toggleBlockSelection(block.type)}
              >
                <div className="w-full flex-col flex">
                  <Text weight="medium">{block.name}</Text>
                  <Text size="1" color="gray">
                    {block.description}
                  </Text>
                </div>
              </Flex>
            ))}
          </Grid>

          <Flex gap="3" mt="4" justify="end">
            <Button
              color="gray"
              highContrast
              onClick={handleAddBlocks}
              disabled={selectedBlocks.length === 0}
            >
              Add{" "}
              {selectedBlocks.length > 0
                ? `${selectedBlocks.length} Blocks`
                : "Blocks"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}

export function CopilotChannelGeneralConfiguration() {
  const [sources, setSources] = useState<string[]>([]);

  const {
    data: configuration,
    isLoading,
    updateStatus,
    update: updateConfiguration,
  } = useCopilotConfiguration();

  const { data: integrations, isLoading: isLoadingIntegrations } =
    useIntegrations();

  const navigate = useNavigate();

  const [views, setViews] = useState<string[]>(["main"]);
  const [selectedView, setSelectedView] = useState("main");
  const configurationRef = useRef(configuration);
  const [items, setItems] = useState<
    { id: string; type: string; defaultLocation?: boolean; view: string }[]
  >([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  function handleUpdateConfiguration(newItems: any[]) {
    updateConfiguration({
      id: configuration?.id ?? "",
      copilotBlocks: newItems.map((x, index) => {
        return {
          ...x,
          order: index,
        };
      }),
      origin: configuration?.origin ?? "",
      tenantId: configuration?.tenantId ?? "",
      createdAt: configuration?.createdAt ?? "",
      updatedAt: configuration?.updatedAt ?? "",
      goal: configuration?.goal ?? "",
      isEnabled: configuration?.isEnabled ?? false,
      isAskAnythingEnabled: configuration?.isAskAnythingEnabled ?? false,
      sources: sources.map((x) => x.split("@")[0]),
    });
  }

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  const sourceOptons = useMemo<SelectOption[]>(() => {
    if (integrations == null) {
      return [];
    }

    return integrations.map((integration) => {
      const { title: label, svg: icon } =
        integrationNameToGalleryItem[integration.name];

      const integrationTypeLabel = () => {
        if (integration.type === "TICKET_SYSTEM") {
          return "Historical Tickets";
        }

        if (integration.type === "KNOWLEDGE_BASE") {
          return "Articles";
        }

        if (integration.type === "TASK_MANAGEMENT") {
          return "Issues and Tasks";
        }

        if (integration.type === "WIKI") {
          return "Internal Documentation";
        }

        if (integration.type === "CHAT") {
          return "Internal Messages";
        }

        return "Knowledge";
      };

      const integrationTypeLable = integrationTypeLabel();

      return {
        value: `${integration.type}@${integration.name}`,
        label: (
          <>
            {label}{" "}
            <Text color="gray">
              <Em>{integrationTypeLable}</Em>
            </Text>
          </>
        ),
        icon,
      };
    });
  }, [integrations]);

  useEffect(() => {
    if (configuration && configuration?.copilotBlocks) {
      setItems(
        configuration?.copilotBlocks
          ?.sort((a, b) => a.order - b.order)
          ?.map((x: any) => {
            const block = Blocks.find((y) => y.type === x.type);
            return {
              ...x,
              name: block?.name ?? x.type,
              description: block?.description ?? x.type,
            };
          })
      );
    }
  }, [JSON.stringify(configuration)]);

  if (isLoading || configuration == null) {
    return <Spinner />;
  }

  if (!configuration) {
    return (
      <Flex align={"center"} height={"100%"} gap={"2"}>
        No configuration found
      </Flex>
    );
  }

  return (
    <Flex gap={"24px"} justify={"between"}>
      <Flex flexGrow={"1"} direction={"column"} gap={"24px"}>
        <AutomationConfigurationContentHeader
          title={"Customize your Copilot Template"}
          status={updateStatus}
          isButtonVisible={true}
          ctaText={"Save"}
          onClick={() => handleUpdateConfiguration(items)}
        />

        <Flex direction={"column"} gap={"4"}>
          {/* <Flex gap={"2"}>
            {views.map((view) => {
              return (
                <Button
                  variant={"outline"}
                  color="gray"
                  key={view}
                  className=" capitalize"
                >
                  <StretchVertical className="w-5 h-5" />
                  {view} View
                </Button>
              );
            })}
            <Tooltip content="The ability to add views is Coming soon">
              <Button
                variant={"ghost"}
                color="gray"
                onClick={() => {
                  setViews([...views, "newView"]);
                }}
                disabled
              >
                <Plus className="w-5 h-5" />
              </Button>
            </Tooltip>
          </Flex> */}

          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <Flex gap={"2"} direction={"column"}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={items}
                    strategy={verticalListSortingStrategy}
                  >
                    <Flex
                      direction={"column"}
                      gap={"2"}
                      maxHeight={"50vh"}
                      overflowY={"auto"}
                      overflowX={"hidden"}
                    >
                      {items.filter((item) => !item.defaultLocation).length ==
                        0 && (
                        <div>
                          <p>No Blocks, Press "Add Block" to add a block</p>
                        </div>
                      )}
                      {items
                        .filter((item) => !item.defaultLocation)
                        .map((item) => (
                          <SortableItem
                            key={item.id}
                            id={item.id}
                            onDelete={() => {
                              setItems(items.filter((i) => i.id !== item.id));
                            }}
                          >
                            <Flex direction={"column"} gap={"1"}>
                              <Text size={"2"} weight={"medium"}>
                                {BlocksMapper[
                                  item.type as keyof typeof BlocksMapper
                                ]?.name ?? item.type}
                              </Text>
                              <Text size={"1"} color={"gray"}>
                                {BlocksMapper[
                                  item.type as keyof typeof BlocksMapper
                                ]?.description ?? item.type}
                              </Text>
                            </Flex>
                          </SortableItem>
                        ))}
                    </Flex>
                  </SortableContext>
                </DndContext>
              </Flex>
              <Flex>
                <Button
                  variant={"outline"}
                  color="gray"
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="w-5 h-5" />
                  Add Block
                </Button>
              </Flex>
            </>
          )}
          <Divider />
          <FieldGroup
            horizontalBigLeft
            label={"Ask Anything"}
            description={
              "Allow your agent to ask any question from the connected sources"
            }
          >
            <Flex flexShrink={"0"}>
              <Switch
                value={items.some((x) => x.type === "askAnything")}
                onChange={(v) => {
                  if (v) {
                    setItems((prev) => [
                      ...prev,
                      {
                        id: "404",
                        type: "askAnything",
                        defaultLocation: true,
                        view: selectedView,
                      },
                    ]);
                  } else {
                    setItems((prev) =>
                      prev.filter((x) => x.type !== "askAnything")
                    );
                  }
                }}
              />
            </Flex>
          </FieldGroup>
          {items.some((x) => x.type === "askAnything") && (
            <>
              <FieldGroup
                label={"Sources 📚"}
                description={
                  "Choose the data sources Quack's Copilot will use to help your agents."
                }
              >
                <MultiSelect
                  options={sourceOptons}
                  value={sources ?? []}
                  onChange={(newValue) => {
                    setSources(newValue);
                  }}
                  loading={isLoadingIntegrations}
                />
              </FieldGroup>
              <Flex gap={"2"}>
                <Text size={"1"} color={"gray"}>
                  For more sources, navigate to our{" "}
                  <Link onClick={() => navigate("/settings")}>
                    integration page
                  </Link>
                </Text>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      <CopilotBlockDialog
        items={items}
        setItems={setItems}
        selectedView={selectedView}
        setDialogOpen={setDialogOpen}
        dialogOpen={dialogOpen}
      />
      <Flex flexShrink={"0"} width={"60%"} justify={"end"}>
        <Copilot views={views} blocks={items} />
      </Flex>
    </Flex>
  );
}

{
  /* <CollapsibleFieldList title={"Blocks"} defaultOpen>
<FieldGroup horizontal description={"User sentiment analysis"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
<FieldGroup horizontal description={"Orgnizational analysis"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
<FieldGroup horizontal description={"Ticket Brief"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
<FieldGroup horizontal description={"Historical ticket analysis"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
<FieldGroup horizontal description={"Suggested sources"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
<FieldGroup horizontal description={"Missing information"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
<FieldGroup horizontal description={"Suggested actions"}>
  <Flex flexShrink={"0"}>
    <Switch value={false} onChange={() => {}} />
  </Flex>
</FieldGroup>
</CollapsibleFieldList>
<hr />
<Flex mt={"4"}>
<Button variant={"ghost"} color="gray" highContrast>
  Add custom block
</Button>
</Flex> */
}
